import React, { useState } from 'react';
import styled from 'styled-components';
import { registrationService } from '../../services/apiService';

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1.5rem;

  img {
    height: 50px;
    width: auto;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  @media (max-width: 768px) {
    gap: 1rem;
    img {
      height: 40px;
    }
  }
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  padding: 2rem;
  text-align: center;
  border-bottom: 3px solid #059669;
  margin-bottom: 2rem;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  h1 {
    margin: 0.5rem 0 0.25rem 0;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  .subtitle {
    font-size: 0.9rem;
    opacity: 0.85;
    margin: 0.5rem 0;
    font-weight: 500;
  }

  .association {
    font-size: 0.8rem;
    opacity: 0.8;
    margin-top: 0.75rem;
    font-style: italic;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    h1 {
      font-size: 1.6rem;
    }
    .subtitle {
      font-size: 0.85rem;
    }
    .association {
      font-size: 0.75rem;
    }
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormWrapper = styled.div`
  padding: 2rem;
`;

const FormTitle = styled.h1`
  color: #333;
  margin-bottom: 2rem;
  text-align: center;
  border-bottom: 3px solid #4caf50;
  padding-bottom: 1rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f9f9f9;
  border-radius: 6px;
  border-left: 4px solid #4caf50;
`;

const SectionTitle = styled.h2`
  color: #4caf50;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '${props => props.icon}';
    font-size: 1.5rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthRow = styled(FormRow)`
  grid-template-columns: 1fr;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
  font-size: 0.95rem;

  span {
    color: #f44336;
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }

  &:hover {
    border-color: #bbb;
  }
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 2px dashed #4caf50;
  border-radius: 4px;
  cursor: pointer;
  background: #f0f0f0;

  &:focus {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
    }
  }

  &.secondary {
    background: #ddd;
    color: #333;

    &:hover {
      background: #bbb;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  background: ${props => props.type === 'error' ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.type === 'error' ? '#c62828' : '#2e7d32'};
  border-left: 4px solid ${props => props.type === 'error' ? '#f44336' : '#4caf50'};
`;

const FileUploadBox = styled.div`
  border: 2px dashed #4caf50;
  border-radius: 6px;
  padding: 1.5rem;
  text-align: center;
  background: #f0f8f4;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #45a049;
    background: #e8f5e9;
  }

  input {
    display: none;
  }
`;

const FileName = styled.p`
  color: #4caf50;
  font-weight: 500;
  margin: 0.5rem 0;
`;

const FormFooter = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
  color: #999;
  font-size: 0.85rem;
  font-weight: 500;
`;

const BuyerRegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [panFile, setPanFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    buyerType: 'Individual',
    companyName: '',
    businessCategory: '',
    state: '',
    region: '',
    organizationName: '',
    addressLine1: '',
    addressLine2: '',
    district: '',
    pincode: '',
    pan: '',
    gst: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e, fileType) => {
    const { files } = e.target;
    if (files[0]) {
      if (fileType === 'pan') {
        setPanFile(files[0]);
      } else {
        setGstFile(files[0]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate password match
      if (formData.password !== formData.confirmPassword) {
        setError('Password and confirm password do not match');
        setLoading(false);
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      await registrationService.registerBuyer(
        formData.phoneNumber,
        formData.email,
        formData.firstName,
        formData.lastName,
        formData.password,
        formData.buyerType,
        formData.companyName,
        formData.businessCategory,
        formData.state,
        formData.region,
        formData.organizationName,
        formData.addressLine1,
        formData.addressLine2,
        formData.district,
        formData.pincode,
        formData.pan,
        formData.gst,
        panFile,
        gstFile
      );

      // For buyer, we can optionally upload RTC if provided
      // Note: RTC is primarily for sellers, but buyer's can have it too

      setSuccess('🎉 Registration successful! You can now login.');
      setFormData({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        buyerType: 'Individual',
        companyName: '',
        businessCategory: '',
        state: '',
        region: '',
        organizationName: '',
        addressLine1: '',
        addressLine2: '',
        district: '',
        pincode: '',
        pan: '',
        gst: '',
      });
      setPanFile(null);
      setGstFile(null);
    } catch (err) {
      setError(err.message || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <LogoContainer>
          <img src="/my-mango-logo.png" alt="Mango Logo" />
        </LogoContainer>
        <h1>🌾 Buyer Seller Platform</h1>
        <div className="subtitle">Register as a Buyer</div>
        <div className="association">Mango Growers' Association, Dharwad</div>
      </PageHeader>

      <FormWrapper>
        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <FormSection>
          <SectionTitle icon="👤">Personal Information</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>First Name <span>*</span></Label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Your first name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Last Name <span>*</span></Label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Your last name"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Phone Number <span>*</span></Label>
              <Input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="+91 9876543210"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email Address <span>*</span></Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="buyer@example.com"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Password <span>*</span></Label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Minimum 6 characters"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Confirm Password <span>*</span></Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Re-enter password"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Business Details */}
        <FormSection>
          <SectionTitle icon="🏢">Business Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Organization Name <span>*</span></Label>
              <Input
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleInputChange}
                placeholder="Your organization name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Buyer Type <span>*</span></Label>
              <Input
                type="text"
                name="buyerType"
                value={formData.buyerType}
                onChange={handleInputChange}
                placeholder="Individual, Company, Organization"
                required
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Business Category <span>*</span></Label>
              <Input
                type="text"
                name="businessCategory"
                value={formData.businessCategory}
                onChange={handleInputChange}
                placeholder="Retail, Wholesale, Distribution"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Company Name</Label>
              <Input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Your company name (if different from org)"
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Tax and Legal Details */}
        <FormSection>
          <SectionTitle icon="📋">Tax & Legal Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>PAN <span>*</span></Label>
              <Input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
                placeholder="AAAPA5055K"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>GST Number <span>*</span></Label>
              <Input
                type="text"
                name="gst"
                value={formData.gst}
                onChange={handleInputChange}
                placeholder="29AAFCU5055K1Z0"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Address Details */}
        <FormSection>
          <SectionTitle icon="🏠">Address Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>Address Line 1 <span>*</span></Label>
              <Input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                placeholder="Street address"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Address Line 2</Label>
              <Input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                placeholder="Additional address details"
              />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>District <span>*</span></Label>
              <Input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleInputChange}
                placeholder="Bangalore"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Pincode <span>*</span></Label>
              <Input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                placeholder="560001"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        {/* Location Details */}
        <FormSection>
          <SectionTitle icon="📍">Location Details</SectionTitle>
          <FormRow>
            <FormGroup>
              <Label>State <span>*</span></Label>
              <Input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Karnataka"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Region <span>*</span></Label>
              <Input
                type="text"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                placeholder="Bangalore"
                required
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        <ButtonGroup>
          <Button className="primary" type="submit" disabled={loading}>
            {loading ? '⏳ Registering...' : '✓ Register as Buyer'}
          </Button>
          </ButtonGroup>
        </form>

        <FormFooter>
          © 2026 Neu Edge Software Private Limited. All rights reserved.
        </FormFooter>
      </FormWrapper>
    </Container>
  );
};

export default BuyerRegistrationForm;
